import EmojiPicker, { EmojiClickData, Theme } from "emoji-picker-react";
import { signInAnonymously } from "firebase/auth";
import {
  collection,
  serverTimestamp,
  addDoc,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import {
  ref,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import React, {
  lazy,
  MutableRefObject,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BsEmojiSmile } from "react-icons/bs";
import { GrFormAttachment } from "react-icons/gr";
import { HiPaperAirplane } from "react-icons/hi";
import { db, auth, imageDb } from "../../api/firebase-config";
import { EcomotoChatType } from "../../types";
import { useCollection } from "react-firebase-hooks/firestore";
import { getFormattedDates } from "../../utils/timeConverter";
import uploadFile from "../../utils/uploadFile";
import { showToast } from "../../utils/showToast";

import { format } from "date-fns";
import { FilePreview } from "../../components";
import { AiOutlineClose } from "react-icons/ai";

type DummyRefType = MutableRefObject<HTMLDivElement | null>;
const LazyMessage = lazy(() => import("../../components/chat/messages"));

interface EcomotoChatProps {
  lesseeId: string;
  lessorId: string;
  rentalID: string;
  onClose: () => void;
}

const EcomotoChat: React.FC<EcomotoChatProps> = ({
  lesseeId,
  lessorId,
  rentalID,
  onClose,
}) => {
  const [newMessage, setNewMessage] = useState("");
  const [openEmojiPicker, setOpenEmojiPicker] = React.useState(false);
  // const user = useSelector((state: any) => state.user.userDetails);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const userID = window.localStorage.getItem("user");
  const dummy: DummyRefType = useRef(null);

  // const lesseeId = "660fe74d8256882e892cd0b7";
  // const lessorId = "660fe74d8256882e892cd0b7";
  // const rentalID = "664f55de2183076ed2c7cdae";
  //This is the message collection from the database
  const messagesRef = collection(db, "messages", rentalID, "messages");

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  // const [previewUrl, setPreviewUrl] = useState<string[]>([]);

  // const profileName = userID && `${user.firstName} ${user.lastName}`;

  const onClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData.isCustom ? emojiData.unified : emojiData.emoji;
    setNewMessage((inputValue) => inputValue + emoji);
  };
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const queryChat = query(messagesRef, orderBy("timestamp", "asc"), limit(80));

  const [messagesSnapshot, loading, error] = useCollection(queryChat);
  // This is used to sign in anonymously.
  // The auth is already declared in the firebase-config
  useEffect(() => {
    const signInAnonymouslyAsync = async () => {
      try {
        if (auth) {
          await signInAnonymously(auth);
          console.log("User signed in anonymously.");
        }
      } catch (err) {
        console.error("Error signing in anonymously:", err);
        if (error?.code === "permission-denied") {
          console.error("Missing or insufficient permissions:", err);
        }
      }
    };

    signInAnonymouslyAsync();
  }, [error?.code]);


  const [progress, setProgress] = useState<number>(0);
  const [fileUrls, setFileUrls] = useState<string[]>([]);

  const [imageLoading, setImageLoading] = React.useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;

    if (selectedFiles) {
      setImageLoading(true);
      // const filesArray = Array.from(selectedFiles);

      const imageFiles: File[] = [];
      const otherFiles: File[] = [];
      Array.from(selectedFiles).forEach((file) => {
        const pdf = file.type === "application/pdf";
        const text = file.type === "text/plain";
        const spreadsheetml = file.type.includes("spreadsheetml");
        const excel = file.type.includes("vnd.ms-excel");

        if (file.type.startsWith("image/")) {
          imageFiles.push(file);
        } else if (file.size > 15 * 1024 * 1024) {
          showToast(`${file.name} is larger than 15 MB.`, "warn");
        } else if (pdf || text || spreadsheetml || excel) {
          otherFiles.push(file);
        } else {
          showToast(`${file.name} is not a supported file type.`, "warn");
        }
      });

      // setFileUrls(otherFiles);
      // const updatedUrls = [...(previewUrl ?? [])];

      // imageFiles.forEach((image) => {
      //   const url = URL.createObjectURL(image);
      //   updatedUrls.push(url);
      // });

      // setPreviewUrl(updatedUrls);

      otherFiles.map(async (file, index) => {
        const filePath = `ecomotoFiles/${file.name}`;

        return new Promise<void>((resolve, reject) => {
          uploadFile(
            file,
            filePath,
            (progress) => setProgress(progress),
            (url) => {
              // setImageUrls((prev) => [...prev, url]);
              setFileUrls((prev) => [...prev, url]);

              resolve();
            },
            (error) => {
              console.error("Upload failed:", error);
              reject(error);
            }
          );
        });
      });
      const uploadPromises = imageFiles.map(async (file, index) => {
        const imgRef = ref(imageDb, `ecomotoImages/${file.size}`);
        const uploadTaskSnapshot = await uploadBytes(imgRef, file);
        const downloadUrl = await getDownloadURL(uploadTaskSnapshot.ref);
        return downloadUrl;
      });

      Promise.all(uploadPromises)
        .then((urls) => {
          // Update imageUrls after all uploads are complete
          setImageUrls((prevUrls) => [...prevUrls, ...urls]);
          // console.log("Uploaded image URLs:", urls);
          // setPreviewUrl((prevUrls) =>
          //   prevUrls ? [...prevUrls, ...urls] : urls
          // );
          // console.log("All images uploaded. Updated imageUrls:", imageUrls);
        })
        .then(() => {
          setImageLoading(false);
        })
        .catch((error) => {
          console.error("Error uploading images:", error);
        });
    }
  };

  //for deleting the image
  const deleteImage = async (imageUrlToDelete: string) => {
    try {
      // Check if imageUrlToDelete is a Blob URL

      // Create a storage reference with the image path as the child reference
      const imageRef = ref(imageDb, imageUrlToDelete);

      // Delete the image from Firebase Storage
      await deleteObject(imageRef);

      // Filter out the deleted image URL from the imageUrls array
      const updatedUrls = imageUrls.filter((url) => url !== imageUrlToDelete);
      const updateFileUrl = fileUrls.filter((url) => url !== imageUrlToDelete);
      // Update the imageUrls state with the updated array
      setImageUrls(updatedUrls);
      setFileUrls(updateFileUrl);

      console.log("Firebase Storage image deleted successfully.");
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  let currentId: string;

  if (userID === lesseeId) {
    currentId = lesseeId;
  } else {
    currentId = lessorId;
  }
 
const [message, setMessage] = useState<any>([]);
  // let prevDate: string | undefined;

  useMemo(() => {
    if (messagesSnapshot) {
      const messages = messagesSnapshot.docs.map((doc) => {
        const myChat = { ...doc.data() } as EcomotoChatType;
        const formattedDate = getFormattedDates(myChat.timestamp);
  
        // Check and update the date
        // if (prevDate === undefined || prevDate !== formattedDate) {
        //   prevDate = formattedDate;
        // }
  
        return {
          ...myChat,
          formattedDate, // Add the formatted date to the message object
        };
      });
  
      // After processing all messages, update the state
      setMessage(messages);
    }
  }, [messagesSnapshot]);
  

  const cleanMessage = (message: string) => {
    let lines = message.split("\n");
    // Trim each line and filter out empty lines
    let cleanedLines = [];

    for (let i = 0; i < lines.length; i++) {
      const trimmedLine = lines[i].trim();
      if (
        trimmedLine.length > 0 ||
        (i < lines.length - 1 && lines[i + 1].trim().length > 0)
      ) {
        cleanedLines.push(lines[i]);
      }
    }

    return cleanedLines.join("\n");
  };

  //This function is used to send the messages
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!lesseeId || !lessorId) return;

    // Validation: Ensure either newMessage or imgUrl is present
    if (!imageUrls.length && !newMessage && !fileUrls.length) {
      console.log("empty field");

      return;
    }

    const cleanedMessage = cleanMessage(newMessage);

    // Add document to Firestore
    await addDoc(messagesRef, {
      text: cleanedMessage,
      timestamp: serverTimestamp(),
      senderID: currentId,
      // receiverID: lessorId,
      imageUrl: imageUrls.length > 0 ? imageUrls : null,
      fileUrls: fileUrls.length > 0 ? fileUrls : null,
    });
    // Reset states and clear input
    setNewMessage("");
    setImageUrls([]);
    setFileUrls([]);
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpenEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  //This is used to target the file input when the icon is clicked.
  const handleIconClick = () => {
    fileInputRef.current!.click();
  };

  const allfileUrls: string[] = [];

  // Convert selected videos to URLs
  imageUrls.forEach((images) => {
    if (images) {
      // console.log(images,"images");
      allfileUrls.push(images);
    }
  });

  // Convert uploaded images to URLs
  fileUrls.forEach((file) => {
    if (file) {
      allfileUrls.push(file);
    }
  });

  return (
    <div className="flex w-full max-w-[600px] items-center justify-center pt-10 mt-20 h-[80vh] shadow-md border-x border-t border bg-white relative">
      <button
        onClick={onClose}
        className="py-2 px-2
          rounded-full text-[#33363F]
          bg-[#ECE6E6] active:bg-bgGrey
          hover:text-gray-600 absolute top-0 right-0"
      >
        <AiOutlineClose size={20} />
      </button>
      <div className="w-full h-full flex flex-col relative">
        <div
          ref={chatContainerRef}
          className="relative w-full h-[calc(100%-40px)] flex flex-col px-[30px] overflow-y-auto"
        >
          {/* {error && <p>Error: {JSON.stringify(error)}</p>} */}

          {!loading &&
            (message || error) &&
            message?.map((chat: EcomotoChatType, index: any) => {
              let prevDate =
                index > 0 ? message[index - 1]?.formattedDate : undefined;
              const date = chat.timestamp
                ? new Date(
                    (chat.timestamp.seconds ?? 0) * 1000 +
                      (chat.timestamp.nanoseconds ?? 0) / 1e6
                  )
                : new Date();
              const currentTime = format(date, "h:mm a"); // Format the time
              const formattedDate = getFormattedDates(chat.timestamp);

              // Check if the current message's date is different from the previous one
              const isDifferentDate = prevDate !== formattedDate;
              prevDate = formattedDate;
              // console.log(chat.imageUrl);

              return (
                <>
                  {isDifferentDate && (
                    <div className="flex justify-center" key={chat.receiverID}>
                      <span className="px-2 py-1 my-2 bg-[#182229] text-[#aebac1] font-light rounded-md text-[9px] font-Poppins">
                        {formattedDate}
                      </span>
                    </div>
                  )}
                  <Suspense>
                    <LazyMessage
                      chat={chat}
                      userID={userID}
                      currentTime={currentTime}
                      key={rentalID}
                    />
                  </Suspense>

                  <div ref={dummy}></div>
                </>
              );
            })}
        </div>
        <div className="flex gap-1 w-full h-fit">
          <FilePreview
            imageLoading={imageLoading}
            imageUrls={allfileUrls}
            progress={progress}
            deleteImage={deleteImage}
            fileUrls={fileUrls}
          />
        </div>

        <form
          className="relative w-full p-3 h-[40px] bg-[#0000000A] justify-around items-center flex mt-3 shadow-md border-x border-t"
          onSubmit={handleSubmit}
        >
          <GrFormAttachment
            size={25}
            className="fill-[#0000000A] cursor-pointer"
            onClick={handleIconClick}
          />
          <input
            type="file"
            ref={fileInputRef}
            multiple
            onChange={handleFileChange}
            className="invisible hidden"
          />
          <textarea
            placeholder="Write a message..."
            onChange={(event) => setNewMessage(event.target.value)}
            value={newMessage}
            rows={4}
            cols={30}
            className="relative lg:w-[88%] w-[70%] h-9 resize-none overflow-y-hidden mx-1 bg-[#0000000A] placeholder:font-SegoeUI placeholder:text-sm font-normal placeholder:text-[#00000099] pt-[9px] outline-none border-none px-5 rounded-3xl"
          />
          <button
            type="submit"
            className=" bg-midGreen h-6 w-6 flex items-center justify-center rounded-full shadow cursor-pointer"
          >
            <HiPaperAirplane fill="white" />
          </button>
          <BsEmojiSmile
            size={22}
            className="cursor-pointer active:fill-red-200 lg:ml-2"
            onClick={() => setOpenEmojiPicker(!openEmojiPicker)}
          />

          {openEmojiPicker && (
            <div
              className="absolute bottom-11 right-[-70px] ease-in-out duration-75 w-[60%] rounded text-[9px] font-Poppins"
              ref={containerRef}
            >
              <EmojiPicker
                onEmojiClick={onClick}
                height={300}
                theme={Theme.AUTO}
                previewConfig={{
                  showPreview: false,
                }}
              />
            </div>
          )}
          {/* <IoMicOutline size={23} className="cursor-pointer" /> */}
        </form>
      </div>
    </div>
  );
};

export default EcomotoChat;
